import React, { useState, useEffect } from 'react'
import { T, TIER, Tabs, Spinner, Empty, Avatar, pct, probColor, confColor, edgeColor, fmtOdds,
         bookName, MARKET_LABEL, VerdictChip, BET_VLEVEL } from './ui'
import { matchupRating } from './cards'
import { sharePick } from './share'
import * as api from './api'

/**
 * NHL player detail — a bottom-sheet drill-in reached by tapping any play card
 * (the MLB/NFL PlayerModal pattern). Everything here is built from the board's
 * own projection payload (prob, line, edge, confidence, the `components`
 * multiplier breakdown) plus a live /nhl/book-odds pull for line-shopping — no
 * new backend. "The Case" turns each clamped multiplier into a plain-English
 * factor so a user sees WHY the model landed where it did.
 */
const defNoun = (mkt) => ({ shots: 'shots', goals: 'goals', points: 'goals',
  assists: 'goals', blocks: 'shot attempts', saves: 'shots', pp_points: 'PP goals' }[mkt] || 'production')

function buildCase(p) {
  const c = p.components || {}
  const m = p.market
  const f = []
  if (c.opp_def_adj != null) {
    if (c.opp_def_adj >= 1.03) f.push(['good', 'Soft matchup', `Opponent allows ~${Math.round((c.opp_def_adj - 1) * 100)}% more ${defNoun(m)} than average`])
    else if (c.opp_def_adj <= 0.97) f.push(['bad', 'Tough matchup', `Opponent allows ~${Math.round((1 - c.opp_def_adj) * 100)}% fewer ${defNoun(m)} than average`])
  }
  if (c.goalie_adj != null && ['goals', 'points', 'assists'].includes(m)) {
    if (c.goalie_adj >= 1.02) f.push(['good', 'Beatable goalie', 'Opposing goalie is below league save %'])
    else if (c.goalie_adj <= 0.98) f.push(['bad', 'Hot goalie', 'Opposing goalie suppresses scoring'])
  }
  if (c.pace_adj != null) {
    if (c.pace_adj >= 1.03) f.push(['good', 'Up-tempo game', 'Both teams drive shot volume'])
    else if (c.pace_adj <= 0.97) f.push(['bad', 'Low-event game', 'Slower pace caps the volume'])
  }
  if (c.pp_unit) f.push([c.pp_unit === 'PP1' ? 'good' : 'neutral', `${c.pp_unit} power play`,
    c.pp_unit === 'PP1' ? 'Top unit — the minutes and linemates that produce power-play points' : 'Second power-play unit'])
  if (c.pp_adj != null && c.pp_adj >= 1.05 && ['pp_points', 'points', 'goals', 'assists'].includes(m))
    f.push(['good', 'Weak penalty kill', 'Opponent PK is below league average'])
  if (c.xg_goal_mult != null && c.xg_goal_mult !== 1.0)
    f.push([c.xg_goal_mult > 1 ? 'good' : 'bad', 'Shot quality (xG)',
      c.xg_goal_mult > 1 ? 'Generates higher-quality chances than average' : 'Lower-quality chances than average'])
  if (c.pvo_adj != null && c.pvo_adj !== 1.0) {
    const d = Math.round(Math.abs(c.pvo_adj - 1) * 100)
    f.push([c.pvo_adj > 1 ? 'good' : 'bad', 'History vs this opponent',
      c.pvo_adj > 1 ? `Produces ~${d}% above his usual rate against them` : `Historically ~${d}% below his usual rate against them`])
  }
  if (c.teammates_out && c.teammates_out.length)
    f.push(['good', 'Next man up', `Out tonight: ${c.teammates_out.join(', ')} — more ice time available`])
  if (c.linemate_adj != null && c.linemate_adj !== 1.0) {
    const d = Math.round(Math.abs(c.linemate_adj - 1) * 100)
    f.push([c.linemate_adj > 1 ? 'good' : 'bad', 'Linemate quality',
      c.linemate_adj > 1 ? `Skating with high-scoring linemates (~+${d}%)` : `Lighter linemates than usual (~-${d}%)`])
  }
  if (Array.isArray(c.intel))
    c.intel.forEach(it => f.push([it.tone || 'neutral',
      it.source === 'scout' ? '🔭 Scout intel' : '📝 Intel', it.note]))
  if (c.b2b) f.push(['bad', 'Back-to-back', 'Second night of a back-to-back — fatigue / goalie risk'])
  else if (c.situational_adj != null && c.situational_adj >= 1.015) f.push(['good', 'Home ice', 'Playing at home'])
  if (c.script_adj != null && c.script_adj >= 1.02 && m === 'blocks')
    f.push(['good', 'Underdog script', 'Trailing teams block more shots'])
  if (m === 'saves' && c.shots_faced != null)
    f.push(['neutral', 'Projected workload', `~${Math.round(c.shots_faced)} shots faced at a ${pct(c.sv_pct)} save rate`])
  if (p.edge_pct != null) {
    if (p.edge_pct >= 2) f.push(['good', 'Beats the price', `Model is +${p.edge_pct}pp vs the book's implied odds`])
    else if (p.edge_pct <= -2) f.push(['bad', 'Priced tight', `Book is ${Math.abs(p.edge_pct)}pp ahead of the model`])
  }
  return f
}

const toneColor = t => t === 'good' ? T.green : t === 'bad' ? T.red : T.muted2
const toneMark = t => t === 'good' ? '✓' : t === 'bad' ? '✕' : '•'

const statVal = (g, mkt) => mkt === 'saves' ? (g.saves ?? 0) : (g[mkt] ?? 0)
const fmtD = d => { if (!d) return ''; const a = d.split('-'); return `${+a[1]}/${+a[2]}` }

function Tile({ label, value, color = T.text }) {
  return (
    <div style={{ flex: 1, background: T.card2, borderRadius: 10, padding: '9px 8px', textAlign: 'center' }}>
      <div style={{ fontFamily: T.mono, fontSize: 17, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 8.5, color: T.muted, letterSpacing: '0.08em', marginTop: 3 }}>{label}</div>
    </div>
  )
}

// "Chance of hitting" waterfall (the NFL BetImpact): league-context baseline -> each
// matchup input in pp -> the model's %, then the edge vs the book. Only rendered when
// the backend could build one that adds up exactly (never for goals/saves).
function BetImpact({ imp, edge }) {
  const max = Math.max(1, ...imp.factors.map(f => Math.abs(f.pp)))
  return (
    <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px', margin: '12px 0' }}>
      <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 8 }}>CHANCE OF HITTING</div>
      <Row label="Baseline (his rate, neutral spot)" right={`${imp.base_pct}%`} />
      {imp.factors.map((f, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
          <span style={{ fontSize: 11.5, color: T.muted2, flex: 1, minWidth: 0 }}>{f.label}</span>
          <span style={{ width: 70, height: 6, background: T.border, borderRadius: 3, position: 'relative', flexShrink: 0 }}>
            <span style={{ position: 'absolute', top: 0, bottom: 0, borderRadius: 3,
              left: f.pp >= 0 ? '50%' : `${50 - 50 * Math.abs(f.pp) / max}%`,
              width: `${50 * Math.abs(f.pp) / max}%`, background: f.pp >= 0 ? T.green : T.red }} />
          </span>
          <span style={{ fontFamily: T.mono, fontSize: 11.5, fontWeight: 800, width: 46, textAlign: 'right',
            color: f.pp >= 0 ? T.green : T.red }}>{f.pp >= 0 ? '+' : ''}{f.pp}</span>
        </div>
      ))}
      <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 6, paddingTop: 6 }}>
        <Row label="Model" right={`${imp.model_pct}%`} bold />
        {edge != null && <Row label="Edge vs book" right={`${edge >= 0 ? '+' : ''}${edge}pp`} color={edgeColor(edge)} bold />}
      </div>
      <div style={{ fontSize: 9.5, color: T.faint, marginTop: 6 }}>Points of hit chance each input adds or removes, at O{imp.line}.</div>
    </div>
  )
}
function Row({ label, right, bold, color = T.text }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
      <span style={{ color: T.muted2, fontWeight: bold ? 800 : 600 }}>{label}</span>
      <span style={{ fontFamily: T.mono, fontWeight: 800, color }}>{right}</span>
    </div>
  )
}

const quant = (a, q) => { if (!a.length) return null; const s = [...a].sort((x, y) => x - y)
  const i = (s.length - 1) * q, lo = Math.floor(i), hi = Math.ceil(i); return s[lo] + (s[hi] - s[lo]) * (i - lo) }
const r1 = v => v == null ? '—' : Math.round(v * 10) / 10
const avg = a => a.length ? a.reduce((x, y) => x + y, 0) / a.length : null

export default function PlayerModal({ p, date, onClose, addLeg, inSlip }) {
  const [tab, setTab] = useState('The Case')
  const [books, setBooks] = useState(null)
  const [form, setForm] = useState(null)
  const [ladder, setLadder] = useState(null)
  useEffect(() => {
    let alive = true
    api.getNHLBookOdds(date, p.market).then(r => {
      if (!alive) return
      // Match by player_id when the odds row has one, else by normalized name — an
      // ambiguous name the resolver couldn't key leaves player_id null on a row that
      // still priced the play, and an id-only filter would wrongly show "no books".
      const norm = s => (s || '').toLowerCase().replace(/[^a-z]/g, '')
      const pn = norm(p.name)
      const rows = (r.odds || []).filter(o =>
        (o.player_id != null && o.player_id === p.player_id) ||
        (o.norm_name && o.norm_name === pn))
      rows.sort((a, b) => (b.over_odds ?? -1e9) - (a.over_odds ?? -1e9))
      setBooks(rows)
    })
    api.getNHLPlayerLogs(p.player_id, 20).then(r => { if (alive) setForm(r) })
    api.getNHLBookOddsLadder(p.player_id, p.market, date).then(r => { if (alive) setLadder(r) })
    return () => { alive = false }
  }, [p.player_id, p.market, date])

  const priced = p.book_line != null
  const line = priced ? p.book_line : p.model_line
  // Unpriced shows model conviction; priced shows the edge-gated recommend tier.
  const dispTier = priced ? p.tier : (p.conviction || p.tier)
  const t = TIER[dispTier] || TIER.PASS
  const factors = buildCase(p)
  const goalMarket = p.market === 'goals'

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 300, background: '#000A',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.bg, width: '100%', maxWidth: 460,
        maxHeight: '92vh', overflowY: 'auto', borderRadius: '18px 18px 0 0', border: `1px solid ${T.border}`,
        borderBottom: 'none', padding: '14px 14px 28px' }}>
        <div style={{ width: 38, height: 4, borderRadius: 3, background: T.border2, margin: '0 auto 12px' }} />

        {/* header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <Avatar url={p.headshot} name={p.name} size={44} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 900, fontSize: 19, color: T.text }}>{p.name}</div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                {p.team} vs {p.opponent} · {p.position || ''} · {MARKET_LABEL[p.market] || p.market}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: T.card2, border: `1px solid ${T.border}`,
            color: T.muted2, borderRadius: 8, width: 30, height: 30, fontSize: 15, cursor: 'pointer', flexShrink: 0 }}>✕</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 7, margin: '10px 0 12px' }}>
          <span style={{ fontSize: 11, fontWeight: 900, color: t.fg, background: t.bg, border: `1px solid ${t.bd}`,
            borderRadius: 6, padding: '3px 9px' }}>{t.icon} {t.label}</span>
          {p.injury && <span style={{ fontSize: 10, fontWeight: 800, color: T.amber, background: T.amberDim,
            border: `1px solid ${T.amber}66`, borderRadius: 5, padding: '2px 7px' }}>Q · {p.injury}</span>}
        </div>

        {/* score strip */}
        <div style={{ display: 'flex', gap: 7, marginBottom: 14 }}>
          <Tile label={priced ? 'HIT %' : 'MODEL %'} value={pct(p.prob_over)} color={probColor(p.prob_over, p.market)} />
          <Tile label="CONFIDENCE" value={p.confidence ?? '—'} color={confColor(p.confidence || 0)} />
          <Tile label="PROJECTION" value={p.proj != null ? p.proj : '—'} />
          <Tile label={goalMarket ? 'MARKET' : (priced ? 'BOOK LINE' : 'PROJ LINE')}
            value={goalMarket ? 'Anytime' : line} color={priced ? T.text : T.amber} />
        </div>

        {/* value vs the price — the NFL block: does the model beat the posted number? */}
        {priced && !p.line_suspect && (
          <div style={{ background: p.value ? T.greenDim : T.card2, border: `1px solid ${p.value ? T.green : T.border}`,
            borderRadius: 10, padding: '9px 12px', marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 3 }}>VALUE VS THE PRICE</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: p.value ? T.green : T.muted2 }}>
              {p.value ? '✓ Beats the price' : 'No value at this price'}
              {p.edge_pct != null && <span style={{ color: edgeColor(p.edge_pct) }}> · {p.edge_pct >= 0 ? '+' : ''}{p.edge_pct}% edge</span>}
              {p.over_odds != null && <span style={{ color: T.muted, fontWeight: 600 }}> · {fmtOdds(p.over_odds)} {bookName(p.book)}</span>}
            </div>
            {p.edge_pct == null && (
              <div style={{ fontSize: 10.5, color: T.faint, marginTop: 3 }}>
                Only one side is posted, so there's no fair price to measure an edge against.</div>
            )}
          </div>
        )}

        <Tabs tabs={['The Case', 'Trends', 'Form', 'Odds']} active={tab} onSelect={setTab} />

        {tab === 'The Case' && (
          <div>
            {p.case && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <VerdictChip label={p.case.verdict} level={BET_VLEVEL[p.case.verdict] || 'neutral'} size={12} />
                <span style={{ fontSize: 11.5, color: T.muted }}>
                  <b style={{ color: T.green }}>{p.case.greens} for</b>{p.case.reds ? <> · <b style={{ color: T.red }}>{p.case.reds} against</b></> : ''}
                </span>
              </div>
            )}
            <div style={{ fontSize: 12.5, color: T.muted2, lineHeight: 1.5, marginBottom: 10 }}>
              The model rates this <b style={{ color: t.bd }}>{t.label}</b> — {pct(p.prob_over)} to go over
              {p.edge_pct != null ? <>, {p.edge_pct >= 0 ? <b style={{ color: T.green }}>+{p.edge_pct}pp vs the book</b> : <b style={{ color: T.red }}>{p.edge_pct}pp vs the book</b>}</> : ''}.
            </div>
            {p.impact && p.impact.factors.length > 0 && <BetImpact imp={p.impact} edge={p.edge_pct} />}
            {factors.length === 0 ? (
              <div style={{ fontSize: 12, color: T.muted, fontStyle: 'italic' }}>
                A neutral spot — no strong matchup factors either way. The read is the base rate.
              </div>
            ) : factors.map(([tone, label, detail], i) => (
              <div key={i} style={{ display: 'flex', gap: 9, padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                <span style={{ color: toneColor(tone), fontWeight: 800, fontSize: 13, width: 14, flexShrink: 0 }}>{toneMark(tone)}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text }}>{label}</div>
                  <div style={{ fontSize: 11.5, color: T.muted, marginTop: 1, lineHeight: 1.4 }}>{detail}</div>
                </div>
              </div>
            ))}
            <div style={{ fontSize: 10, color: T.faint, marginTop: 12, lineHeight: 1.5 }}>
              Factors are the model's clamped matchup inputs, in plain English. Information only — not betting advice. 21+.
            </div>
          </div>
        )}

        {tab === 'Trends' && (
          form === null ? <Spinner /> :
          (form.logs || []).length === 0 ? <Empty message="No game log yet this season." /> :
          (() => {
            const logs = form.kind === 'goalie' ? form.logs.filter(g => g.started !== false) : form.logs
            const vals = logs.map(g => statVal(g, p.market))
            const hitOf = v => goalMarket ? v >= 1 : v > line
            const n = vals.length, hits = vals.filter(hitOf).length
            const l5 = avg(vals.slice(0, 5)), all = avg(vals)
            const home = logs.filter(g => g.home).map(g => statVal(g, p.market))
            const road = logs.filter(g => !g.home).map(g => statVal(g, p.market))
            const trend = l5 != null && all ? (l5 - all) / all : 0
            const mr = matchupRating(p)
            const hr = n ? hits / n : 0
            return (
              <div>
                <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '0.08em', margin: '2px 0 6px' }}>
                  HIT RATE AT THIS LINE</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <span style={{ fontFamily: T.mono, fontSize: 20, fontWeight: 900, color: probColor(hr, p.market) }}>{hits}/{n}</span>
                  <span style={{ flex: 1, height: 7, background: T.border, borderRadius: 4, overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', width: `${hr * 100}%`, background: probColor(hr, p.market) }} />
                  </span>
                  <span style={{ fontSize: 11.5, color: T.muted2 }}>{goalMarket ? 'scored' : `cleared over ${line}`}</span>
                </div>
                <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 6 }}>
                  RANGE · LAST {n}</div>
                <div style={{ display: 'flex', gap: 7, marginBottom: 14 }}>
                  <Tile label="FLOOR" value={r1(quant(vals, 0.2))} />
                  <Tile label="MEDIAN" value={r1(quant(vals, 0.5))} />
                  <Tile label="CEILING" value={r1(quant(vals, 0.8))} />
                  <Tile label="PROJ" value={p.proj != null ? p.proj : '—'} color={T.ice} />
                </div>
                <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 4 }}>TREND & SPLITS</div>
                <Row label="Last 5 avg" right={r1(l5)} />
                <Row label={`Last ${n} avg`} right={r1(all)} />
                <Row label="Form" right={Math.abs(trend) < 0.1 ? 'Steady' : trend > 0 ? `▲ Heating up (+${Math.round(trend * 100)}%)` : `▼ Cooling (${Math.round(trend * 100)}%)`}
                  color={Math.abs(trend) < 0.1 ? T.muted2 : trend > 0 ? T.green : T.amber} />
                {form.kind !== 'goalie' && logs[0]?.toi != null && (
                  <Row label="Ice time · last 5 vs all" right={`${r1(avg(logs.slice(0, 5).map(g => g.toi || 0)))}′ vs ${r1(avg(logs.map(g => g.toi || 0)))}′`} />
                )}
                <Row label={`Home (${home.length})`} right={home.length ? `${r1(avg(home))} · ${home.filter(hitOf).length}/${home.length}` : '—'} />
                <Row label={`Road (${road.length})`} right={road.length ? `${r1(avg(road))} · ${road.filter(hitOf).length}/${road.length}` : '—'} />
                <Row label={`Tonight's matchup vs ${p.opponent}`} right={mr.label} color={mr.color} />
                <div style={{ fontSize: 10, color: T.faint, marginTop: 10 }}>
                  From this season's game logs, graded vs the {priced ? 'book' : 'projected'} line shown. Past results don't predict future ones.
                </div>
              </div>
            )
          })()
        )}

        {tab === 'Form' && (
          form === null ? <Spinner /> :
          (form.logs || []).length === 0 ? <Empty message="No game log yet this season." /> :
          (() => {
            const logs = form.logs
            const hitOf = g => { const v = statVal(g, p.market); return goalMarket ? v >= 1 : v > line }
            const hits = logs.filter(hitOf).length
            const fv = logs.map(g => statVal(g, p.market))
            return (
              <div>
                <div style={{ display: 'flex', gap: 7, marginBottom: 12 }}>
                  <Tile label="GAMES" value={logs.length} />
                  <Tile label={`${(MARKET_LABEL[p.market] || p.market).toUpperCase()}/G`} value={r1(avg(fv))} />
                  <Tile label={goalMarket ? 'SCORED' : 'CLEARED'} value={`${hits}/${logs.length}`}
                    color={hits / logs.length >= 0.5 ? T.green : T.amber} />
                </div>
                <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '0.08em', margin: '2px 0 8px' }}>
                  LAST {logs.length} GAMES · <b style={{ color: hits / logs.length >= 0.5 ? T.green : T.amber }}>
                    {hits}/{logs.length}</b> {goalMarket ? 'scored' : `over O${line}`}
                </div>
                {logs.map((g, i) => {
                  const v = statVal(g, p.market)
                  const hit = hitOf(g)
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '7px 2px', borderBottom: `1px solid ${T.border}`, fontSize: 12 }}>
                      <span style={{ color: T.muted2, width: 92, flexShrink: 0 }}>
                        {fmtD(g.date)} {g.home ? 'vs' : '@'} {g.opponent}
                      </span>
                      <span style={{ color: T.faint, fontSize: 11, fontFamily: T.mono }}>
                        {form.kind === 'goalie' ? `${g.saves}/${g.shots_against} sv` : `${g.toi ?? ''}′`}
                      </span>
                      <span style={{ fontFamily: T.mono, fontWeight: 800, color: hit ? T.green : T.red, width: 54, textAlign: 'right' }}>
                        {v} {hit ? '✓' : '✗'}
                      </span>
                    </div>
                  )
                })}
                <div style={{ fontSize: 10, color: T.faint, marginTop: 10 }}>
                  Graded vs the {priced ? 'book' : 'projected'} line shown. Past results don't predict future ones.
                </div>
              </div>
            )
          })()
        )}

        {tab === 'Odds' && (
          <div>
            {books === null ? <Spinner /> :
              books.length === 0 ? (
                <div style={{ fontSize: 12, color: T.muted, fontStyle: 'italic', padding: '10px 0' }}>
                  No book has posted this market yet. The line above is our <b style={{ color: T.amber }}>projected</b> number.
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '0.08em', margin: '2px 0 8px' }}>
                    BEST PRICE FIRST · LINE-SHOP ACROSS BOOKS
                  </div>
                  {books.map((o, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 2px', borderBottom: `1px solid ${T.border}`, fontSize: 12.5 }}>
                      <span style={{ color: i === 0 ? T.green : T.muted2, fontWeight: i === 0 ? 800 : 600 }}>
                        {i === 0 ? '💰 ' : ''}{bookName(o.book)}
                      </span>
                      <span style={{ color: T.muted2 }}>Over <b style={{ fontFamily: T.mono }}>{o.line}</b></span>
                      <span style={{ fontFamily: T.mono, fontWeight: 800, color: i === 0 ? T.green : T.text }}>
                        {fmtOdds(o.over_odds)}
                      </span>
                    </div>
                  ))}
                  {p.no_vig_prob != null && (
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 10 }}>
                      Consensus fair (de-vigged): <b style={{ color: T.text }}>{pct(p.no_vig_prob)}</b> · our model {pct(p.prob_over)}.
                    </div>
                  )}
                  {!goalMarket && ladder && ladder.rungs && ladder.rungs.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 6 }}>
                        VALUE ACROSS THE LADDER</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr 1fr 1fr', fontSize: 9.5, color: T.faint,
                        fontWeight: 700, letterSpacing: '0.06em', padding: '0 4px 4px' }}>
                        <span>LINE (OVER)</span><span>BEST</span><span style={{ textAlign: 'right' }}>MODEL</span><span style={{ textAlign: 'right' }}>EDGE</span>
                      </div>
                      {ladder.rungs.map((r, i) => {
                        const star = ladder.best_value && ladder.best_value.line === r.line
                        const mp = r.shown_prob != null ? r.shown_prob : r.model_prob
                        return (
                          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr 1fr 1fr', alignItems: 'center',
                            padding: '7px 4px', fontSize: 12, borderBottom: `1px solid ${T.border}`,
                            background: r.selected ? T.card2 : 'transparent', borderRadius: r.selected ? 6 : 0 }}>
                            <span style={{ fontFamily: T.mono, fontWeight: 800, color: T.text }}>
                              {star ? <span style={{ color: T.green }}>★ </span> : null}{r.line}</span>
                            <span style={{ color: T.muted2 }}><b style={{ fontFamily: T.mono, color: T.text }}>{fmtOdds(r.best_over)}</b>
                              <span style={{ fontSize: 10.5 }}> {bookName(r.best_book)}</span></span>
                            <span style={{ textAlign: 'right', fontFamily: T.mono, color: mp != null ? probColor(mp, p.market) : T.faint }}>
                              {mp != null ? pct(mp) : '—'}</span>
                            <span style={{ textAlign: 'right', fontFamily: T.mono, fontWeight: 800, color: edgeColor(r.edge_pct) }}>
                              {r.edge_pct != null ? `${r.edge_pct >= 0 ? '+' : ''}${r.edge_pct}` : '—'}</span>
                          </div>
                        )
                      })}
                      <div style={{ fontSize: 10, color: T.faint, marginTop: 6, lineHeight: 1.45 }}>
                        Only lines a book actually posted. Edge needs 2+ books pricing both sides of that line — otherwise
                        it's one book's vig, so we show —. {ladder.best_value ? `★ = where the model most beats the price.` : ''}
                      </div>
                    </div>
                  )}
                </>
              )}
          </div>
        )}

        <div style={{ background: T.card2, borderRadius: 6, padding: '6px 10px', marginTop: 14, fontSize: 11,
          display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ color: p.status === 'questionable' || p.status === 'returning' ? T.amber : T.green }}>
            {p.status === 'questionable' ? `⚠ Questionable${p.injury ? ` — ${p.injury}` : ''}`
              : p.status === 'returning' ? `↩ ${p.note || 'Back from an absence — role & minutes uncertain'}` : '✓ Active'}</span>
          <span style={{ color: T.muted }}>{p.market === 'saves' ? 'Starters confirm ~morning skate' : 'Lineups post ~morning skate'}</span>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          {addLeg && (
            <button onClick={() => addLeg(p)} disabled={inSlip} style={{ flex: 1, background: inSlip ? T.card2 : T.green,
              color: inSlip ? T.muted2 : '#04140D', border: inSlip ? `1px solid ${T.border2}` : 'none',
              borderRadius: 10, padding: '11px', fontSize: 13, fontWeight: 800, cursor: inSlip ? 'default' : 'pointer' }}>
              {inSlip ? '✓ In slip' : '🎯 Add to slip'}
            </button>
          )}
          <button onClick={() => sharePick(p)} style={{ flex: 1, background: T.greenDim,
            color: T.green, border: `1px solid ${T.green}66`, borderRadius: 10, padding: '11px', fontSize: 13,
            fontWeight: 800, cursor: 'pointer' }}>↗ Share</button>
        </div>
      </div>
    </div>
  )
}
